import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from "react-redux";

import * as actionTypes from '../../store/actions/actionTypes';
import Theme_WelcomeLogo from "../../components/Theme/Theme_WelcomeLogo";
import { useDropzone } from "react-dropzone";
import { Turnstile } from "@marsidev/react-turnstile";
import {handleLogin} from "../../utils/auth";
import axios from "axios";
import dotProp from "dot-prop-immutable";

const BookingPage = () => {
    const [token, setToken] = useState("");
    const [configs, setConfigs] = useState({
        turnstile_site_key: '',
        cdn_path: '',
        file_booking_logo: '',
        hospitals: [],
        ticketId: '',
        patient_portal_theme: {},
    });
    const [formData, setFormData] = useState({
        hospital: "",
        surgeon: "",
        firstName: "",
        surname: "",
        patientMobile: "",
        patientEmail: "",
        procedureReason: "",
        dob: "",
        procedureDate: "",
        bookingFiles: [],
        turnstileToken: ""
    });
    const [loading, setLoading] = useState(false);
    const [dropzoneKey, setDropzoneKey] = useState(0);
    const [errorDropzone, setErrorDropzone] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [files, setFiles] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const ref = React.useRef();

    const resetTurnstile = () => ref.current?.reset()

    useEffect(() => {
        if (configs.patient_portal_theme.booking_windowTitle_enable) {
            document.title = configs.patient_portal_theme.booking_windowTitle_value;
        }
    }, [configs]);

    useEffect(() => {
        getInitBookingPage();

        const link = document.createElement("link");
        link.href = "https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css";
        link.rel = "stylesheet";
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const getInitBookingPage = () => {
        axios.post(`/api/patient/booking/start`)
            .then((response) => {
                if (response.data) {
                    setConfigs(response.data);
                }
            })
    }

    // When only one hospital is available there is no choice to make, so pre-select it automatically.
    useEffect(() => {
        if (configs.hospitals.length === 1) {
            setFormData(prevFormData => ({ ...prevFormData, hospital: configs.hospitals[0].id }));
        }
    }, [configs.hospitals]);

    const handleChange = (e) => {
        setFormData(prevFormData => ({ ...prevFormData, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e) => {
        setFormData(prevFormData => ({ ...prevFormData, bookingFiles: Array.from(e.target.files) }));
    };

    const handleTurnstileSuccess = (token) => {
        setFormData(prevFormData => ({ ...prevFormData, turnstileToken: token }));
    };

    const setTicketId = (value) => {
        setConfigs(prevFormData => ({ ...prevFormData, ticketId: value }));
    };

    const validateForm = () => {
        let newErrors = {};
        const today = new Date().toISOString().split("T")[0];

        if (!formData.hospital) newErrors.hospital = "Please select a hospital.";
        if (!formData.surgeon) newErrors.surgeon = "Surgeon name is required.";
        if (!formData.firstName) newErrors.firstName = "First name is required.";
        if (!formData.surname) newErrors.surname = "Surname is required.";
        // if (!formData.patientEmail) {
        //     newErrors.patientEmail = "Email is required.";
        // } else
        if (formData.patientEmail && !/\S+@\S+\.\S+/.test(formData.patientEmail)) {
            newErrors.patientEmail = "Email format is invalid.";
        }
        if (!formData.dob) newErrors.dob = "Date of birth is required.";
        else if (formData.dob >= today) newErrors.dob = "Date of birth must be in the past.";

        if (!formData.procedureDate) newErrors.procedureDate = "Procedure date is required.";
        else if (formData.procedureDate <= today) newErrors.procedureDate = "Procedure date must be in the future.";

        if (!formData.turnstileToken) newErrors.turnstileToken = "Captcha verification is required.";

        setErrors(newErrors);
        console.log('validate', newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const maxFiles = 3;
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        key: dropzoneKey,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".jpg", ".jpeg", ".png"]
        },
        maxFiles: maxFiles,
        onDrop: (acceptedFiles) => {
            if ((acceptedFiles.length + files.length) > maxFiles) {
                console.log(acceptedFiles);
                setErrorDropzone(`You can only upload up to ${maxFiles} files.`);
                return;
            }
            setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
            setErrorDropzone("");
        }
    });

    const removeFile = (fileIndex) => {
        setFiles(files.filter((_, index) => index !== fileIndex));
    };

    const handleSubmit = async (e) => {
        console.log('Handle submit');
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccessMessage("");
        setErrorDropzone("");

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("hospital", formData.hospital);
        formDataToSend.append("surgeon", formData.surgeon);
        formDataToSend.append("firstName", formData.firstName);
        formDataToSend.append("surname", formData.surname);
        formDataToSend.append("phone", formData.patientMobile);
        formDataToSend.append("email", formData.patientEmail);
        formDataToSend.append("dob", formData.dob);
        formDataToSend.append("procedureDate", formData.procedureDate);
        formDataToSend.append("procedureReason", formData.procedureReason);
        formDataToSend.append("turnstileToken", formData.turnstileToken);
        formDataToSend.append("ticketId", configs.ticketId);

        files.forEach((file, index) => {
            formDataToSend.append(`files[${index}]`, file);
        });

        try {
            const response = await axios.post("/api/patient/booking/create", formDataToSend, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setSuccessMessage("Form submitted successfully! Ticket ID: " + configs.ticketId);
            setFormData({
                hospital: (configs.hospitals.length === 1) ? configs.hospitals[0].id : "",
                surgeon: "",
                firstName: "",
                surname: "",
                patientMobile: "",
                patientEmail: "",
                procedureReason: "",
                dob: "",
                procedureDate: "",
                bookingFiles: [],
                turnstileToken: ""
            });
            setDropzoneKey((prevKey) => prevKey + 1);
            setFiles([]);
            resetTurnstile();
            setTicketId(response.data.new_ticketId);
        } catch (error) {
            console.log(error)
            if (error.response) {
                setError(error.response.data.message || "An error occurred. Please try again.");
            } else {
                setError("Failed to submit the form. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="booking-page bg-gray-100 p-4">
            <div className="form-container mx-auto bg-white p-4 rounded-xl shadow-lg">

                {configs.file_booking_logo && (
                    <div className="flex justify-center mb-4">
                        <img src={`${configs.cdn_path}${configs.file_booking_logo}`} alt="logo"/>
                    </div>
                )}

                <form onSubmit={handleSubmit} method="POST" encType="multipart/form-data">
                    {configs.hospitals.length > 1 && (
                        <div className="mb-4">
                            <label htmlFor="hospital" className="block text-sm font-semibold text-gray-700">Admitting Hospital</label>
                            <select id="hospital" name="hospital" value={formData.hospital} onChange={handleChange} className={`w-full p-2 border rounded text-black mt-1 ${errors.hospital ? "error" : "border-gray-300"}`} required>
                                <option value="">Select a hospital</option>
                                {configs.hospitals.map((hospital) => (
                                    <option key={hospital.id} value={hospital.id}>
                                        {hospital.title}
                                    </option>
                                ))}
                            </select>
                            {errors.hospital && <p className="text-red-500 text-sm mt-1">{errors.hospital}</p>}
                        </div>
                    )}

                    <div className="mb-4">
                        <label htmlFor="surgeon" className="block text-sm font-semibold text-gray-700">{configs.patient_portal_theme.booking_labels_surgeon}</label>
                        <input type="text" id="surgeon" name="surgeon" value={formData.surgeon} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.surgeon ? "error" : "border-gray-300"}`} required/>
                        {errors.surgeon && <p className="text-red-500 text-sm mt-1">{errors.surgeon}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700">Patient First Name</label>
                        <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.firstName ? "error" : "border-gray-300"}`} required/>
                        {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="surname" className="block text-sm font-semibold text-gray-700">Patient Surname</label>
                        <input type="text" id="surname" name="surname" value={formData.surname} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.surname ? "error" : "border-gray-300"}`} required/>
                        {errors.surname && <p className="text-red-500 text-sm mt-1">{errors.surname}</p>}
                    </div>
                    <div className="mb-4">
                        <label htmlFor="patientMobile" className="block text-sm font-semibold text-gray-700">Patient Mobile</label>
                        <input type="text" id="patientMobile" name="patientMobile" value={formData.patientMobile} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.patientMobile ? "error" : "border-gray-300"}`} required/>
                        {errors.patientMobile && <p className="text-red-500 text-sm mt-1">{errors.patientMobile}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="dob" className="block text-sm font-semibold text-gray-700">Patient DOB</label>
                        <input type="date" id="dob" name="dob" value={formData.dob} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.dob ? "error" : "border-gray-300"}`} required/>
                        {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="procedureDate" className="block text-sm font-semibold text-gray-700">{configs.patient_portal_theme.booking_labels_dateOfProcedure}</label>
                        <input type="date" id="procedureDate" name="procedureDate" value={formData.procedureDate} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.procedureDate ? "error" : "border-gray-300"}`} required/>
                        {errors.procedureDate && <p className="text-red-500 text-sm mt-1">{errors.procedureDate}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="procedureReason" className="block text-sm font-semibold text-gray-700">{configs.patient_portal_theme.booking_labels_reasonForProcedure}</label>
                        <textarea id="procedureReason" name="procedureReason" value={formData.procedureReason} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.procedureReason ? "error" : "border-gray-300"}`}/>
                        {errors.procedureReason && <p className="text-red-500 text-sm mt-1">{errors.procedureReason}</p>}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="patientEmail" className="block text-sm font-semibold text-gray-700">Practice Email</label>
                        <input type="email" id="patientEmail" name="patientEmail" value={formData.patientEmail} onChange={handleChange} className={`w-full p-2 border rounded mt-1 text-black ${errors.patientEmail ? "error" : "border-gray-300"}`}/>
                        {errors.patientEmail && <p className="text-red-500 text-sm mt-1">{errors.patientEmail}</p>}
                    </div>
                    {/*<div className="mb-4">*/}
                    {/*    <label htmlFor="patientEmail" className="block text-sm font-semibold text-gray-700">Ticket Id</label>*/}
                    {/*    {configs.ticketId}*/}
                    {/*</div>*/}

                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700">Signed RFA (Optional)</label>

                        <div {...getRootProps()} className="border border-gray-300 text-black py-5 rounded mt-1 text-center cursor-pointer">
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <p>Drop the files here ...</p>
                            ) : (
                                <div>
                                    <p>Drag and drop files here or click to select</p>
                                    <em>(Max {maxFiles} files, allowed: PDF, JPG, PNG)</em>
                                </div>
                            )}
                            {isDragReject && <p className="text-red-500">Some files are not supported</p>}
                        </div>
                        {errorDropzone && <p className="text-red-500 mt-2">{errorDropzone}</p>}

                        <ul className="mt-2">
                            {files.map((file, index) => (
                                <li key={index} className="flex justify-between items-center bg-gray-100 p-2 rounded mt-1">
                                    <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="text-red-500 font-bold text-right w-auto"
                                    >
                                        ✖
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {configs.turnstile_site_key && (
                        <div className="mb-4">
                            {errors.turnstileToken && <p className="text-red-500 mt-2">{errors.turnstileToken}</p>}
                            <Turnstile
                                ref={ref}
                                siteKey={configs.turnstile_site_key}
                                onSuccess={handleTurnstileSuccess}
                            />
                        </div>
                    )}

                    <div className="mb-4">
                        {error && <p className="text-red-500 mt-2">{error}</p>}
                        {successMessage && <p className="text-green-500 mt-2">{successMessage}</p>}
                    </div>

                    <div>
                        <button
                            type="submit"
                            className={`bg-blue-500 text-white p-2 rounded-lg w-full ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"}`}
                            disabled={loading}
                        >
                            {loading ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default BookingPage;